import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedFxTradeDetail, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-fx-trade-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, CustomerSelectorComponent, CurrencySelectorComponent,
    AinAmountPipe, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="pageTitle()" [description]="t('sales.regulated.fx.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="layout">
          <section class="main">
            <header class="card"><a class="back-link" [routerLink]="['/sales/fx-trades']">{{ t('catalog.action.backToList') }}</a></header>
            @if (isQuoteMode()) {
              <section class="card grid">
                <label><span>{{ t('sales.regulated.fx.fields.baseCurrency') }}</span><ain-currency-selector [(ngModel)]="quoteForm.baseCurrency" /></label>
                <label><span>{{ t('sales.regulated.fx.fields.quoteCurrency') }}</span><ain-currency-selector [(ngModel)]="quoteForm.quoteCurrency" /></label>
                <label><span>{{ t('sales.regulated.fx.fields.rate') }}</span><p-inputNumber [(ngModel)]="quoteForm.rate" [min]="0" /></label>
                <button pButton type="button" [label]="t('sales.regulated.fx.createQuote')" (click)="createQuote()"></button>
              </section>
            } @else if (isCreate()) {
              <section class="card grid">
                <label><span>{{ t('sales.regulated.fx.fields.baseCurrency') }}</span><ain-currency-selector [(ngModel)]="form.baseCurrency" /></label>
                <label><span>{{ t('sales.regulated.fx.fields.quoteCurrency') }}</span><ain-currency-selector [(ngModel)]="form.quoteCurrency" /></label>
                <label><span>{{ t('sales.regulated.fx.fields.baseAmount') }}</span><p-inputNumber [(ngModel)]="form.baseAmount" [min]="0" /></label>
                <label><span>{{ t('sales.regulated.fx.fields.rate') }}</span><p-inputNumber [(ngModel)]="form.rate" [min]="0" /></label>
                <label><span>{{ t('sales.regulated.gold.fields.quoteId') }}</span><input pInputText [(ngModel)]="form.quoteId" /></label>
                <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
                <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              </section>
            } @else if (trade(); as tr) {
              <section class="summary card">
                <article><span>{{ t('columns.status') }}</span><strong>{{ tr.status }}</strong></article>
                <article><span>{{ t('sales.regulated.fx.fields.pair') }}</span><strong>{{ tr.baseCurrency }}/{{ tr.quoteCurrency }}</strong></article>
                <article><span>{{ t('sales.regulated.fx.fields.baseAmount') }}</span><strong>{{ tr.baseAmount | ainAmount }}</strong></article>
                <article><span>{{ t('sales.regulated.fx.fields.settled') }}</span><strong>{{ tr.settledQuoteAmount | ainAmount }}</strong></article>
              </section>
            }
          </section>
          @if (!isCreate() && !isQuoteMode() && trade()) {
            <ain-sales-ai-copilot-panel entityType="FxTrade" [entityId]="trade()!.id" />
          }
        </div>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesFxTradeDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly isQuoteMode = signal(false);
  readonly trade = signal<RegulatedFxTradeDetail | null>(null);
  readonly lastQuoteId = signal<string | null>(null);
  form = { baseCurrency: 'USD', quoteCurrency: 'IRR', baseAmount: 100, rate: 1, quoteId: null as string | null, customerAccountId: null as string | null };
  quoteForm = { baseCurrency: 'USD', quoteCurrency: 'IRR', rate: 1, validForMinutes: 15 };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('tradeId') ?? '';
    const mode = this.route.snapshot.data['mode'] as string | undefined;
    this.isQuoteMode.set(mode === 'quote' || id === 'new-quote');
    this.isCreate.set(!this.isQuoteMode() && (!id || id === 'new'));
    const quoteId = this.route.snapshot.queryParamMap.get('quoteId');
    if (quoteId) this.form.quoteId = quoteId;
    if (this.isCreate() || this.isQuoteMode()) { this.loading.set(false); return; }
    this.api.getFxTrade(id).subscribe({
      next: tr => { this.trade.set(tr); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  pageTitle(): string {
    if (this.isQuoteMode()) return this.t('sales.regulated.fx.newQuote');
    if (this.isCreate()) return this.t('sales.regulated.fx.newTrade');
    return this.t('sales.regulated.fx.detailTitle');
  }
  createQuote(): void {
    this.api.createFxQuote(this.quoteForm).subscribe({
      next: q => {
        this.lastQuoteId.set(q.catalogQuoteId);
        void this.router.navigate(['/sales/fx-trades/new'], { queryParams: { quoteId: q.catalogQuoteId } });
      }
    });
  }
  save(): void {
    this.api.executeFxTrade(this.form).subscribe({
      next: tr => void this.router.navigate(['/sales/fx-trades', tr.id])
    });
  }
  t(key: string): string { return this.localization.translate(key); }
}
